<template>
    <div class="LogosGrid md:mt-10" :class="{ 'md:mt-0': embedded }">
        <div :class="wrapperClass">
            <slot name="title" />

            <div class="mt-6 grid w-full justify-center gap-4" :class="computedGridClass">
                <template v-for="(logo, index) in logos" :key="index">
                    <a
                        v-if="logo.href"
                        :class="logoItemClass"
                        :href="logo.href"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <img
                            :alt="logo.title"
                            class="mx-auto object-contain"
                            :class="[computedImageClass(logo.size || 'small'), { 'grayscale': isDobroweek }]"
                            :src="getLogoSrc(logo.img)"
                            :title="logo.title"
                        />
                    </a>
                    <div v-else :class="logoItemClass">
                        <img
                            :alt="logo.title"
                            class="mx-auto object-contain"
                            :class="[computedImageClass(logo.size || 'small'), { 'grayscale': isDobroweek }]"
                            :src="getLogoSrc(logo.img)"
                            :title="logo.title"
                        />
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Logo } from '@/constants/logos'

interface Props {
    logos: Logo[]
    gridClass?: string
    imagesPath?: string
    isDobroweek?: boolean
    variant?: 'default' | 'twoRows' | 'singleRow'
    embedded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    gridClass: 'grid-cols-4 lg:grid-cols-8',
    imagesPath: 'coop',
    isDobroweek: false,
    variant: 'default',
    embedded: false
})

const wrapperClass = computed((): string => {
    return props.embedded
        ? 'my-0 flex flex-col items-center gap-y-4'
        : 'container my-10 flex flex-col items-center gap-y-10'
})

const computedGridClass = computed((): string | object => {
    if (props.isDobroweek) {
        if (props.variant === 'singleRow') {
            return 'grid-cols-3 sm:grid-cols-4 lg:grid-flow-col lg:auto-cols-fr items-center gap-2 sm:gap-3 md:gap-4'
        }
        return props.variant === 'twoRows'
            ? 'grid-cols-2 md:grid-cols-4 gap-8'
            : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-12'
    }
    return props.gridClass
})

const logoItemClass = computed((): string => {
    if (props.isDobroweek && props.variant === 'singleRow') {
        return 'block w-full min-w-0'
    }
    return ''
})

const computedImageClass = (size: string): string => {
    if (props.isDobroweek) {
        if (props.variant === 'singleRow') {
            return size === 'big'
                ? 'h-10 w-full sm:h-11 md:h-12'
                : 'h-9 w-full sm:h-10 md:h-11'
        }
        if (props.variant === 'twoRows') {
            return size === 'big' ? 'h-24 w-40' : 'h-20 w-32'
        }
        return size === 'big' ? 'h-20 w-52 md:h-28 md:w-60' : 'h-16 w-44 md:h-20 md:w-52'
    }
    const defaultSize = 'h-12 w-18'
    return size === 'big' ? 'h-18 w-24' : defaultSize
}

const getLogoSrc = (imageName: string): string => {
    try {
        // In Nuxt 3, we need to use the public directory for static assets
        return `/images/${props.imagesPath}/${imageName}`
    } catch (error) {
        console.error(`Failed to load image: ${imageName}`, error)
        return ''
    }
}
</script>

<style lang="postcss" scoped>
.grayscale {
    -webkit-filter: grayscale(100%);
    filter: grayscale(100%);
}
</style>
